import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  GenericIconName,
  Icon,
  IconSize,
  UnstyledButton,
  Text,
  Z_INDEXES,
  Drawer,
  DrawerBody,
  tabletAndUp,
  FONT_SIZES,
  Modal,
} from '@aboutyou/ui-components';
import React, {FC, useEffect} from 'react';
import {useCouponWalletStateManager} from './hooks/useCouponWalletStateManager';
import styled, {css} from 'styled-components';
import {CouponWalletBodyContent} from './CouponWalletBodyContent';
import {useTranslation} from '@aboutyou/i18n';
import {useFullSession} from '@aboutyou/user';
import {useIsCouponWalletActive} from './hooks/useIsCouponWalletActive';
import {useTrackContentView} from '@aboutyou/tracking';
import {useHistory} from '@aboutyou/router';

const BACK_ICON_SIZE = 'S';

interface CouponWalletProps {
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
}

export const CouponWallet: FC<CouponWalletProps> = ({
  ContentPopperLayoutComponent,
}) => {
  const shouldShowHeaderInBody = ContentPopperLayoutComponent !== Drawer;

  const {isOpen, cancel} = useCouponWalletStateManager();
  const fullSession = useFullSession();
  const isCouponWalletActive = useIsCouponWalletActive();
  const trackContentView = useTrackContentView();
  const history = useHistory();

  useEffect(() => {
    if (isOpen) {
      trackContentView({
        pageTitle: 'coupon_wallet',
        overridePageProps: {
          type: 'coupon_wallet',
        },
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const currentHistoryState: Record<string, unknown> | {} =
      history.location.state || {};
    history.replace({
      pathname: history.location.pathname,
      search: history.location.search,
      state: {...currentHistoryState, walletSeen: true},
    });
  }, [history]);

  if (!fullSession || !isCouponWalletActive) {
    return null;
  }

  // animated only exists on Modal
  const animatedProp =
    ContentPopperLayoutComponent === Modal
      ? {
          animated: history.location.state
            ? !(history.location?.state as {walletSeen: boolean}).walletSeen
            : true,
        }
      : {};

  return (
    <StyledContentPopper
      LayoutComponent={ContentPopperLayoutComponent}
      onClose={cancel}
      slideDirection="left"
      open={isOpen}
      renderInPortal
      showCloseIcon={false}
      {...(animatedProp || {})}
      dataTestId="couponWallet"
      preventSlideUpAnimation
      headerContent={!shouldShowHeaderInBody && <Header hideBackButton />}
      bodyContent={
        <Wrapper>
          {shouldShowHeaderInBody && <Header />}
          <CouponWalletBodyContent />
        </Wrapper>
      }
    />
  );
};

const Header: FC<{hideBackButton?: boolean}> = ({hideBackButton}) => {
  const {t} = useTranslation();
  const {cancel} = useCouponWalletStateManager();

  return (
    <HeaderWrapper data-testid="notificationCenter-header">
      {!hideBackButton && (
        <BackButton onClick={cancel}>
          <Icon asset={GenericIconName.ArrowLeft} size={BACK_ICON_SIZE} />
        </BackButton>
      )}
      <TextContainer>
        <Title size="px20" weight="mediumHeavy">
          {t('Your Coupons')}
        </Title>
      </TextContainer>
    </HeaderWrapper>
  );
};

const Wrapper = styled.div`
  --button-left-padding: 13px;
  --button-right-padding: 9px;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  align-items: center;
  display: flex;
  padding-block: 13px;
  position: sticky;
  top: 0;
  background-color: ${COLORS.White};
  z-index: ${Z_INDEXES.FLYOUT_STICKY_HEADER};
`;

const BackButton = styled(UnstyledButton)`
  padding-inline: var(--button-left-padding) var(--button-right-padding);
  z-index: 1;
`;

const TextContainer = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  padding-right: calc(
    var(--button-left-padding) + var(--button-right-padding) +
      ${IconSize[BACK_ICON_SIZE]}px
  );
  ${IconSize[BACK_ICON_SIZE]}
`;

const Title = styled(Text)`
  line-height: 1.2;

  ${tabletAndUp(css`
    font-size: ${FONT_SIZES.px16};
    line-height: 1.25;
  `)}
`;

const StyledContentPopper = styled(ContentPopper)`
  ${DrawerBody} {
    padding: 0;
  }
`;
